/**
 * Context-chip model and ⇄ assistant-ui attachment encoding.
 *
 * Context chips (incident/device/view/scope) are pinned to the composer via
 * assistant-ui's attachment system rather than ad hoc local state. This gives
 * us, for free: attachments travel with the specific message they were sent
 * with, render above that message in the thread, and are cleared from the
 * composer automatically once the message is sent — matching the pinned
 * context UX used by GitHub Copilot.
 */

import type { CompleteAttachment } from "@assistant-ui/react";

export type ContextChip = {
  id: string;
  label: string;
  displayValue: string;
  entityId?: string;
  entityType?:
    | "incident"
    | "device"
    | "alarm"
    | "site"
    | "dashboard"
    | "panel"
    | "view"
    | "scope";
  icon?: string;
};

const CONTEXT_CHIP_CONTENT_TYPE_PREFIX = "insaion/context-chip";

export function chipToAttachment(chip: ContextChip): CompleteAttachment {
  return {
    id: chip.id,
    type: "document",
    name: `${chip.label}: ${chip.displayValue}`,
    contentType: `${CONTEXT_CHIP_CONTENT_TYPE_PREFIX};icon=${chip.icon ?? "sparkles"}`,
    content: [
      {
        type: "text",
        text: JSON.stringify({
          label: chip.label,
          displayValue: chip.displayValue,
          entityId: chip.entityId,
          entityType: chip.entityType,
          icon: chip.icon,
        }),
      },
    ],
    status: { type: "complete" },
  };
}

export function attachmentToChip(attachment: {
  id: string;
  name: string;
  contentType?: string;
  content?: readonly unknown[];
}): ContextChip {
  let label = attachment.name;
  let displayValue = "";
  let entityId: string | undefined;
  let entityType: ContextChip["entityType"];
  let icon: string | undefined;

  const iconMatch = attachment.contentType?.match(/icon=([\w-]+)/);
  if (iconMatch) icon = iconMatch[1];

  const first = attachment.content?.[0] as
    | { type?: string; text?: string }
    | undefined;
  if (first?.type === "text" && first.text) {
    try {
      const parsed = JSON.parse(first.text);
      if (parsed && typeof parsed === "object") {
        if (typeof parsed.label === "string") label = parsed.label;
        if (typeof parsed.displayValue === "string") {
          displayValue = parsed.displayValue;
        } else if (typeof parsed.value === "string") {
          displayValue = isUuid(parsed.value)
            ? "Context unavailable"
            : parsed.value;
        }
        if (typeof parsed.entityId === "string") entityId = parsed.entityId;
        if (typeof parsed.entityType === "string") {
          entityType = parsed.entityType as ContextChip["entityType"];
        }
        if (typeof parsed.icon === "string") icon = parsed.icon;
      }
    } catch {
      // Fall back to name/contentType-derived values.
    }
  }

  return { id: attachment.id, label, displayValue, entityId, entityType, icon };
}

function isUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
    value,
  );
}

// ─── Page-aware context suggestions ──────────────────────────────────────────

export type PageContextNames = {
  deviceName?: string;
  siteId?: string;
  siteName?: string;
  alarmName?: string;
  dashboardName?: string;
  panelTitle?: string;
};

export function getPageContextSuggestions(
  pathname: string,
  names: PageContextNames = {},
): ContextChip[] {
  const deviceMatch = pathname.match(/^\/devices\/([^/]+)$/);
  if (deviceMatch) {
    const suggestions: ContextChip[] = [];
    if (names.deviceName) {
      suggestions.push({
        id: "device",
        label: "Device",
        displayValue: names.deviceName,
        entityId: deviceMatch[1],
        entityType: "device",
        icon: "cpu",
      });
    }
    if (names.siteName) {
      suggestions.push({
        id: "site",
        label: "Site",
        displayValue: names.siteName,
        entityId: names.siteId,
        entityType: "site",
        icon: "file",
      });
    }
    return suggestions;
  }

  const alarmMatch = pathname.match(/^\/alarms\/([^/]+)\/edit$/);
  if (alarmMatch) {
    return names.alarmName
      ? [
          {
            id: "alarm",
            label: "Alarm",
            displayValue: names.alarmName,
            entityId: alarmMatch[1],
            entityType: "alarm",
            icon: "bell",
          },
        ]
      : [];
  }

  const dashboardMatch = pathname.match(/^\/monitoring\/([^/]+)/);
  const panelMatch = pathname.match(/^\/monitoring\/[^/]+\/panel\/([^/]+)/);
  if (dashboardMatch) {
    const suggestions: ContextChip[] = [];
    if (names.dashboardName) {
      suggestions.push({
        id: "dashboard",
        label: "Dashboard",
        displayValue: names.dashboardName,
        entityId: dashboardMatch[1],
        entityType: "dashboard",
        icon: "activity",
      });
    }
    if (panelMatch && names.panelTitle) {
      suggestions.push({
        id: "panel",
        label: "Panel",
        displayValue: names.panelTitle,
        entityId: panelMatch[1],
        entityType: "panel",
        icon: "file",
      });
    }
    return suggestions;
  }

  if (pathname === "/monitoring") {
    return [
      {
        id: "view",
        label: "View",
        displayValue: "Fleet Monitoring",
        entityType: "view",
        icon: "activity",
      },
    ];
  }
  if (pathname.includes("/alarms")) {
    return [
      {
        id: "view",
        label: "View",
        displayValue: "Active Alarms",
        entityType: "view",
        icon: "bell",
      },
    ];
  }
  if (pathname.includes("/recordings")) {
    return [
      {
        id: "view",
        label: "View",
        displayValue: "Recordings",
        entityType: "view",
        icon: "file",
      },
    ];
  }
  return [];
}
